import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import Breadcrumbs from '../../components/Breadcrumbs';
import { PrimaryButton } from '../../components/Buttons';
import ErrorMessage from '../../components/ErrorMessage';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, PageTitle } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import SectionHeader from '../../components/SectionHeader';
import useModal from '../../hooks/useModal';
import { sanitizeHTML } from '../../utils/sanitizeHTML';
import { useUser } from '../authentication';
import ConfirmationModal from '../../components/ConfirmationModal';
import { ModalWarningList } from '../../components/ModalElements';
import { FeedbackTask } from '../steps';
import AddFeedbackRecipientModal from './AddFeedbackRecipientModal';
import EmptyFeedbackCard from './EmptyFeedbackCard';
import FeedbackRecipientCard from './FeedbackRecipientCard';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import { useFeedbackViewer } from './hooks/useFeedbackViewer';

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${Grid._5};
  margin-bottom: ${Grid._6};

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardStatusTab = styled.div<{ $status?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Grid._2};
  background-color: ${({ $status }) =>
    $status === 'complete' ? Color.Primary._50 : $status === 'in_progress' ? '#f0ad4e' : Color.Gray._30};
  border-radius: 4px 4px 0 0;
  padding: ${Grid._4};
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Grid._3};
  margin: 0 0 ${Grid._5} 0;
`;

const StatusSummary = styled.span`
  font-size: ${Type.Scale._2};
  color: ${Type.Color.medium};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Grid._4};
  margin-bottom: ${Grid._3};

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageHeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const TaskDescription = styled.p`
  color: ${Type.Color.medium};
  font-size: ${Type.Scale._3};
  margin-bottom: ${Grid._5};
`;

export default function FeedbackViewer() {
  const {
    id: taskId,
    taskListInstanceId,
    isPersonal: isPersonalParam,
  } = useParams<{ id: string; taskListInstanceId: string; isPersonal?: string }>();
  const { user } = useUser();
  const isPersonal = isPersonalParam === 'true';
  const { step, requests, setRequests, resetFeedbackUser, resendFeedbackUser, markFeedbackTaskComplete } =
    useFeedbackViewer(taskListInstanceId, taskId, user?.userId);

  const feedbackTask = step?.data?.task as FeedbackTask;
  const courseId = step?.id;

  const taskDescription = feedbackTask?.taskDescription ?? '';
  const taskName = feedbackTask?.taskName;
  const responsesReqForComplete = feedbackTask?.responsesReqForComplete ?? 0;
  const numCompleteResponses = requests.filter(r => r.status === 'complete').length;
  const numIncompleteResponses = requests.length - numCompleteResponses;

  const handleAddSlotInvite = (values: { email: string; firstName: string }) => {
    setRequests(prev => {
      const index = prev.findIndex(r => !r.status);
      if (index === -1) return prev;
      const next = [...prev];
      next[index] = { ...values, status: 'not_started', responseId: '' };
      return next;
    });
  };

  const [modal, openModal] = useModal((type: string, payload: any, dismissModal: () => void) => {
    if (type === 'markComplete') {
      const n = payload.numIncompleteResponses as number;
      const incompleteWord = n === 1 ? 'response' : 'responses';
      return (
        <ConfirmationModal
          title="Mark as Complete"
          prompt={`Are you sure you want to complete this feedback step with ${n} incomplete ${incompleteWord}?`}
          subtext="Force completing this step will prevent any future invitations from being completed and will result in an incomplete and less accurate report for the church planting organization that is viewing your report."
          buttonActionText="Complete"
          handleDismiss={dismissModal}
          handleSubmit={() => {
            payload.onConfirm();
            dismissModal();
          }}
        />
      );
    }
    if (type === 'resetFeedback') {
      const entry = payload.entry as { firstName: string; email: string };
      return (
        <ConfirmationModal
          buttonActionText="Reset"
          buttonType="danger"
          title="Reset recipient"
          prompt={<span>Are you sure you want to reset this recipient?</span>}
          subtext={[entry.firstName, entry.email].filter(Boolean).join(' — ')}
          handleDismiss={dismissModal}
          handleSubmit={() => {
            payload.onConfirm();
            dismissModal();
          }}
        >
          <ModalWarningList warnings={['This action cannot be undone']} />
        </ConfirmationModal>
      );
    }
    if (type === 'addContact' && step?.data && courseId && user?.userId && feedbackTask) {
      return (
        <AddFeedbackRecipientModal
          courseId={courseId}
          step={step.data}
          onAddRequest={values => handleAddSlotInvite(values)}
          handleDismiss={dismissModal}
        />
      );
    }
    return null;
  });

  const canMarkComplete =
    step?.data?.status !== 'complete' &&
    ((!isPersonal && numCompleteResponses >= 1) || numCompleteResponses >= responsesReqForComplete);

  const completionSummary = `${numCompleteResponses} of ${requests.length} completed`;

  const statusLabel = (status: string) => {
    if (status === 'complete') return 'Complete';
    if (status === 'in_progress') return 'In Progress';
    return 'Not Complete';
  };

  const requestStatusLabel = (status?: string) => {
    if (status === 'complete') return 'Completed';
    if (status === 'in_progress') return 'In Progress';
    return 'Not Started';
  };

  const crumbs = [
    { name: 'Training', route: `#/training-viewer/course/${taskListInstanceId}` },
    { name: taskName ?? 'Feedback' },
  ];

  if (step?.isLoading) return <LoadingState />;

  if (step?.isError) return <ErrorMessage />;

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />

      <Container style={{ paddingTop: Grid._6, paddingBottom: Grid._6 }}>
        <PageHeader>
          <PageTitle>
            <h1>{taskName ?? 'Feedback'}</h1>
          </PageTitle>
          <PageHeaderActions>
            {canMarkComplete && (
              <PrimaryButton
                onClick={() =>
                  openModal('markComplete', { numIncompleteResponses, onConfirm: markFeedbackTaskComplete })
                }
              >
                <i className="fas fa-check" />
                Mark as Complete
              </PrimaryButton>
            )}
            {step?.data?.status === 'complete' && <PrimaryButton disabled>Step Marked Complete</PrimaryButton>}
          </PageHeaderActions>
        </PageHeader>
        <StatusRow>
          <FeedbackStatusBadge status={step?.data?.status ?? ''} label={statusLabel(step?.data?.status ?? '')} />
          <StatusSummary>{completionSummary}</StatusSummary>
        </StatusRow>
        {taskDescription && <TaskDescription dangerouslySetInnerHTML={{ __html: sanitizeHTML(taskDescription) }} />}
        <SectionHeader title={'Recipients'} />
        <HelpAndInfoBox
          iconClass="fas fa-info-circle"
          description={
            isPersonal
              ? 'Requests may be reset or re-sent only if the Recipient has Not Yet Started their Feedback.'
              : 'This feedback step may be marked as complete by an administrator when at least one request is complete.'
          }
        />
        <CardsGrid>
          {requests.map((entry, index) => {
            const isSent = !!entry.status;

            return (
              <CardWrapper key={index}>
                {isSent && (
                  <CardStatusTab $status={entry.status}>
                    <FeedbackStatusBadge status={entry.status} label={requestStatusLabel(entry.status)} />
                  </CardStatusTab>
                )}

                {isSent ? (
                  <FeedbackRecipientCard
                    firstName={entry.firstName}
                    lastName={entry.email}
                    onReset={() =>
                      openModal('resetFeedback', {
                        entry,
                        index,
                        onConfirm: () => resetFeedbackUser(entry, index),
                      })
                    }
                    onResend={() => resendFeedbackUser(entry)}
                    showActions={entry.status === 'not_started' || entry.status === 'in_progress'}
                  />
                ) : (
                  <EmptyFeedbackCard onClick={() => openModal('addContact')} />
                )}
              </CardWrapper>
            );
          })}
        </CardsGrid>
      </Container>

      {modal}
    </>
  );
}
